import { ALL_PERMISSION_KEYS, hasPermission, PERMISSION_GROUPS } from '@serviceportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Plus, ShieldCheck, Trash2, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Badge, Card, Checkbox, Input, Select, Skeleton, Textarea } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Audience, RoleItem } from '@/lib/types';

/** Aktuell ausgelieferte Ausbauphase – spätere Module sind bereits konfigurierbar. */
const CURRENT_PHASE = 8;

function expand(patterns: string[]) {
  return ALL_PERMISSION_KEYS.filter((k) => hasPermission(patterns, k));
}

/** Fasst vollständig gewählte Präfixe wieder zu `präfix.*` zusammen. */
export function compress(keys: string[]): string[] {
  const set = new Set(keys);
  const prefixes = [...new Set(ALL_PERMISSION_KEYS.map((k) => k.split('.')[0]!))];
  const result: string[] = [];
  for (const prefix of prefixes) {
    const all = ALL_PERMISSION_KEYS.filter((k) => k.split('.')[0] === prefix);
    const chosen = all.filter((k) => set.has(k));
    if (chosen.length === all.length) result.push(`${prefix}.*`);
    else result.push(...chosen);
  }
  return result;
}

function RoleEditor({ role, open, onOpenChange }: { role: RoleItem | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [audience, setAudience] = useState<Audience>('INTERNAL');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const fullAccess = role?.permissions.includes('*') ?? false;

  useEffect(() => {
    if (!open) return;
    setName(role?.name ?? '');
    setDescription(role?.description ?? '');
    setAudience(role?.audience ?? 'INTERNAL');
    setSelected(new Set(expand(role?.permissions ?? [])));
  }, [open, role]);

  const groups = PERMISSION_GROUPS.filter((g) =>
    audience === 'CUSTOMER' ? g.audience === 'customer' : g.audience === 'internal' || (g.audience === 'platform' && me?.tenant.isPlatform),
  );

  const toggle = (keys: string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => (on ? next.add(k) : next.delete(k)));
      return next;
    });

  const mutation = useMutation({
    mutationFn: () => {
      const permissions = fullAccess ? role!.permissions : compress([...selected].filter((k) => groups.some((g) => g.permissions.some((p) => p.key === k))));
      const body = { name, description: description || null, audience, permissions };
      return role ? api(`/roles/${role.id}`, { method: 'PATCH', body }) : api('/roles', { method: 'POST', body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      toast.success('Rolle gespeichert');
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent wide>
        <DialogHeader>
          <DialogTitle>{role ? `Rolle „${role.name}“ bearbeiten` : 'Neue Rolle'}</DialogTitle>
          <DialogDescription>Sie können nur Berechtigungen vergeben, die Sie selbst besitzen. Änderungen wirken sofort.</DialogDescription>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" htmlFor="r-name">
              <Input id="r-name" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Zielgruppe" htmlFor="r-aud" hint={role?.isSystem ? 'Bei Systemrollen nicht änderbar' : undefined}>
              <Select
                id="r-aud"
                value={audience}
                disabled={role?.isSystem}
                onChange={(e) => {
                  setAudience(e.target.value as Audience);
                  setSelected(new Set());
                }}
              >
                <option value="INTERNAL">Mitarbeiter</option>
                <option value="CUSTOMER">Kunden (Portal)</option>
              </Select>
            </Field>
            <Field label="Beschreibung" htmlFor="r-desc" className="sm:col-span-2">
              <Textarea id="r-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
          </div>

          {fullAccess ? (
            <div className="flex items-center gap-3 rounded-lg border bg-muted/40 p-4 text-sm">
              <Lock className="size-4 text-muted-foreground" />
              Diese Rolle besitzt Vollzugriff auf alle Funktionen der Plattform.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {groups.map((g) => {
                const keys = g.permissions.map((p) => p.key);
                const count = keys.filter((k) => selected.has(k)).length;
                const state = count === 0 ? false : count === keys.length ? true : 'indeterminate';
                return (
                  <div key={g.module} className="rounded-lg border">
                    <label className="flex cursor-pointer items-center gap-3 border-b bg-muted/30 px-3 py-2">
                      <Checkbox checked={state} onCheckedChange={(c) => toggle(keys, c === true)} />
                      <span className="text-sm font-semibold">{g.label}</span>
                      {g.availableFrom > CURRENT_PHASE && <Badge variant="outline">Modul ab Phase {g.availableFrom}</Badge>}
                      <span className="ml-auto text-xs text-muted-foreground">
                        {count}/{keys.length}
                      </span>
                    </label>
                    <div className="grid gap-x-4 gap-y-2 p-3 sm:grid-cols-2">
                      {g.permissions.map((p) => (
                        <label key={p.key} className="flex cursor-pointer items-start gap-2.5 text-sm">
                          <Checkbox className="mt-0.5" checked={selected.has(p.key)} onCheckedChange={(c) => toggle([p.key], c === true)} />
                          <span>
                            {p.label}
                            <span className="block font-mono text-[11px] text-muted-foreground">{p.key}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={name.trim().length < 2}>
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RolesPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const canManage = can('admin.roles.manage');
  const [editor, setEditor] = useState<{ open: boolean; role: RoleItem | null }>({ open: false, role: null });
  const [confirmDialog, confirm] = useConfirm();
  const { data: roles, isLoading } = useQuery({ queryKey: ['roles'], queryFn: () => api<RoleItem[]>('/roles') });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/roles/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      toast.success('Rolle gelöscht');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const sections = useMemo(
    () => [
      { label: 'Mitarbeiterrollen', items: roles?.filter((r) => r.audience === 'INTERNAL') ?? [] },
      { label: 'Kundenrollen', items: roles?.filter((r) => r.audience === 'CUSTOMER') ?? [] },
    ],
    [roles],
  );

  return (
    <>
      <PageHeader
        title="Rollen & Rechte"
        description="Rollen bündeln Berechtigungen und werden Benutzern zugewiesen."
        actions={
          canManage && (
            <Button onClick={() => setEditor({ open: true, role: null })}>
              <Plus /> Neue Rolle
            </Button>
          )
        }
      />
      {isLoading && <Skeleton className="h-64" />}
      {sections.map((s) => (
        <section key={s.label} className="mb-8">
          <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted-foreground">{s.label}</h2>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {s.items.map((r) => (
              <Card key={r.id} className="flex flex-col p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="size-4 text-primary" />
                    <h3 className="font-semibold">{r.name}</h3>
                  </div>
                  {r.isSystem && <Badge variant="outline">System</Badge>}
                </div>
                {r.description && <p className="mt-1.5 text-sm text-muted-foreground">{r.description}</p>}
                <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Users className="size-3.5" /> {r.userCount} Benutzer
                  </span>
                  <span>{r.permissions.includes('*') ? 'Vollzugriff' : `${expand(r.permissions).length} Berechtigungen`}</span>
                </div>
                {canManage && (
                  <div className="mt-4 flex gap-2 border-t pt-3">
                    <Button size="sm" variant="outline" onClick={() => setEditor({ open: true, role: r })}>
                      Bearbeiten
                    </Button>
                    {!r.isSystem && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        disabled={r.userCount > 0}
                        title={r.userCount > 0 ? 'Rolle ist noch Benutzern zugeordnet' : undefined}
                        onClick={async () => {
                          if (await confirm({ title: `Rolle „${r.name}“ löschen?`, confirmLabel: 'Löschen', destructive: true })) remove.mutate(r.id);
                        }}
                      >
                        <Trash2 /> Löschen
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </section>
      ))}
      {confirmDialog}
      {canManage && <RoleEditor open={editor.open} role={editor.role} onOpenChange={(open) => setEditor((e) => ({ ...e, open }))} />}
    </>
  );
}
